import { useEffect, useState } from 'react'
import { Truck, Plus, Edit3, Trash2, Search, Mail, Phone, MapPin } from 'lucide-react'

interface Supplier {
  id: string
  slug: string
  name: string
  contactName: string | null
  email: string | null
  phone: string | null
  city: string | null
  state: string | null
  rfc: string | null
  paymentTerms: string | null
  leadTimeDays: number | null
  minOrderAmount: number | null
  isActive: boolean
  skuCount: number
}

const blank = {
  id: '', slug: '', name: '', contactName: '', email: '', phone: '',
  address: '', city: '', state: '', rfc: '', paymentTerms: '',
  leadTimeDays: '', minOrderAmount: '', notes: '', isActive: true,
}

export function ProveedoresLlantas() {
  const [items, setItems] = useState<Supplier[]>([])
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [editing, setEditing] = useState<typeof blank | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function load() {
    setLoading(true)
    const r = await fetch(`/api/admin/proveedores-llantas?q=${encodeURIComponent(q)}`)
    const d = await r.json()
    setItems(d.items || [])
    setLoading(false)
  }
  useEffect(() => { load() }, [])

  async function save() {
    setSaving(true); setError('')
    const isEdit = !!editing!.id
    const payload: any = {
      name: editing!.name,
      contactName: editing!.contactName || null,
      email: editing!.email || null,
      phone: editing!.phone || null,
      address: editing!.address || null,
      city: editing!.city || null,
      state: editing!.state || null,
      rfc: editing!.rfc || null,
      paymentTerms: editing!.paymentTerms || null,
      leadTimeDays: editing!.leadTimeDays ? parseInt(editing!.leadTimeDays as any) : null,
      minOrderAmount: editing!.minOrderAmount ? parseFloat(editing!.minOrderAmount as any) : null,
      notes: editing!.notes || null,
      isActive: editing!.isActive,
    }
    if (!isEdit) payload.slug = editing!.slug
    const url = isEdit ? `/api/admin/proveedores-llantas/${editing!.id}` : '/api/admin/proveedores-llantas'
    const method = isEdit ? 'PATCH' : 'POST'
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    setSaving(false)
    if (!res.ok) {
      const d = await res.json().catch(() => ({}))
      setError(d.error || `HTTP ${res.status}`)
      return
    }
    setEditing(null)
    load()
  }

  async function remove(s: Supplier) {
    const msg = s.skuCount > 0
      ? `${s.name} tiene ${s.skuCount} SKUs. Se marcará como INACTIVO. Continuar?`
      : `Eliminar permanentemente el proveedor ${s.name}?`
    if (!confirm(msg)) return
    await fetch(`/api/admin/proveedores-llantas/${s.id}`, { method: 'DELETE' })
    load()
  }

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-extrabold text-ink-900">
            <Truck className="size-6 text-brand-600" /> Proveedores de llantas
          </h1>
          <p className="text-sm text-ink-500">
            {items.length} proveedores · Mayoristas, distribuidores, importadores
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing({ ...blank })}
          className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700"
        >
          <Plus className="size-4" /> Nuevo proveedor
        </button>
      </header>

      <div className="rounded-2xl border border-ink-200 bg-white p-4">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-400" />
          <input
            type="search"
            placeholder="Buscar proveedor…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            className="w-full rounded-lg border border-ink-200 bg-white pl-9 pr-3 py-2 text-sm"
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {loading ? (
          <div className="col-span-full py-8 text-center text-ink-400">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="col-span-full rounded-2xl border border-dashed border-ink-300 bg-white p-10 text-center">
            <Truck className="mx-auto mb-3 size-10 text-ink-300" />
            <p className="font-semibold text-ink-700">Sin proveedores aún</p>
            <p className="text-sm text-ink-500">Crea el primer proveedor mayorista de llantas.</p>
          </div>
        ) : items.map((s) => (
          <div key={s.id} className="rounded-2xl border border-ink-200 bg-white p-4">
            <div className="mb-3 flex items-start justify-between">
              <div>
                <div className="font-bold text-ink-900">{s.name}</div>
                <div className="font-mono text-xs text-ink-500">{s.slug}</div>
              </div>
              {s.isActive
                ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">Activo</span>
                : <span className="rounded-full bg-ink-100 px-2 py-0.5 text-xs">Inactivo</span>}
            </div>

            {s.contactName && <div className="text-sm text-ink-700">{s.contactName}</div>}
            <div className="mt-2 space-y-1 text-xs text-ink-600">
              {s.email && <div className="flex items-center gap-1.5"><Mail className="size-3.5" />{s.email}</div>}
              {s.phone && <div className="flex items-center gap-1.5"><Phone className="size-3.5" />{s.phone}</div>}
              {(s.city || s.state) && <div className="flex items-center gap-1.5"><MapPin className="size-3.5" />{[s.city, s.state].filter(Boolean).join(', ')}</div>}
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5 text-[10px]">
              {s.paymentTerms && <span className="rounded bg-brand-50 px-1.5 py-0.5 font-semibold text-brand-700">{s.paymentTerms}</span>}
              {s.leadTimeDays && <span className="rounded bg-amber-50 px-1.5 py-0.5 font-semibold text-amber-700">{s.leadTimeDays}d lead</span>}
              <span className="rounded bg-ink-100 px-1.5 py-0.5 font-semibold text-ink-700">{s.skuCount} SKUs</span>
            </div>

            <div className="mt-3 flex justify-end gap-1">
              <button onClick={() => setEditing({ ...s, leadTimeDays: s.leadTimeDays?.toString() || '', minOrderAmount: s.minOrderAmount?.toString() || '', address: '', notes: '', contactName: s.contactName || '', email: s.email || '', phone: s.phone || '', city: s.city || '', state: s.state || '', rfc: s.rfc || '', paymentTerms: s.paymentTerms || '' } as any)}
                className="rounded p-1.5 text-ink-500 hover:bg-brand-50 hover:text-brand-700">
                <Edit3 className="size-4" />
              </button>
              <button onClick={() => remove(s)} className="rounded p-1.5 text-ink-500 hover:bg-red-50 hover:text-red-700">
                <Trash2 className="size-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/50 p-4">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6">
            <h2 className="mb-4 text-lg font-extrabold text-ink-900">
              {editing.id ? 'Editar proveedor' : 'Nuevo proveedor'}
            </h2>
            <form onSubmit={(e) => { e.preventDefault(); save() }} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                {!editing.id && (
                  <Field label="Slug (kebab-case) *">
                    <input required pattern="[a-z0-9-]+" value={editing.slug}
                      onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                      className="form-input" />
                  </Field>
                )}
                <Field label="Nombre comercial *">
                  <input required value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    className="form-input" />
                </Field>
                <Field label="Contacto">
                  <input value={editing.contactName}
                    onChange={(e) => setEditing({ ...editing, contactName: e.target.value })}
                    className="form-input" />
                </Field>
                <Field label="Email">
                  <input type="email" value={editing.email}
                    onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                    className="form-input" />
                </Field>
                <Field label="Teléfono">
                  <input value={editing.phone}
                    onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
                    className="form-input" />
                </Field>
                <Field label="RFC">
                  <input value={editing.rfc}
                    onChange={(e) => setEditing({ ...editing, rfc: e.target.value })}
                    placeholder="ABC123456X9A" className="form-input" />
                </Field>
                <Field label="Ciudad">
                  <input value={editing.city}
                    onChange={(e) => setEditing({ ...editing, city: e.target.value })}
                    className="form-input" />
                </Field>
                <Field label="Estado">
                  <input value={editing.state}
                    onChange={(e) => setEditing({ ...editing, state: e.target.value })}
                    className="form-input" />
                </Field>
              </div>
              <Field label="Dirección">
                <input value={editing.address}
                  onChange={(e) => setEditing({ ...editing, address: e.target.value })}
                  className="form-input" />
              </Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Términos de pago">
                  <select value={editing.paymentTerms}
                    onChange={(e) => setEditing({ ...editing, paymentTerms: e.target.value })}
                    className="form-input">
                    <option value="">—</option>
                    <option value="Contado">Contado</option>
                    <option value="Crédito 15">Crédito 15</option>
                    <option value="Crédito 30">Crédito 30</option>
                    <option value="Crédito 45">Crédito 45</option>
                    <option value="Crédito 60">Crédito 60</option>
                  </select>
                </Field>
                <Field label="Tiempo entrega (días)">
                  <input type="number" value={editing.leadTimeDays}
                    onChange={(e) => setEditing({ ...editing, leadTimeDays: e.target.value })}
                    className="form-input" />
                </Field>
                <Field label="Monto mínimo">
                  <input type="number" step="0.01" value={editing.minOrderAmount}
                    onChange={(e) => setEditing({ ...editing, minOrderAmount: e.target.value })}
                    className="form-input" />
                </Field>
              </div>
              <Field label="Notas internas">
                <textarea rows={2} value={editing.notes}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                  className="form-input" />
              </Field>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.isActive}
                  onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })} />
                Activo
              </label>

              {error && <div className="rounded-lg bg-red-50 p-2 text-sm text-red-700">{error}</div>}

              <div className="flex justify-end gap-2 pt-3">
                <button type="button" onClick={() => setEditing(null)}
                  className="rounded-lg border border-ink-200 bg-white px-4 py-2 text-sm font-semibold">
                  Cancelar
                </button>
                <button type="submit" disabled={saving}
                  className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50">
                  {saving ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-ink-700">{label}</span>
      {children}
    </label>
  )
}
